"use client";

import * as m from "motion/react-m";

import { Orb } from "@ui/components/core/Orb/Orb";
import type { OrbTextureName } from "@ui/components/core/Orb/textures";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { Button } from "react-aria-components";

interface SpeechEngineEnableButtonProps {
  className?: string;
  onPress?: () => void;
  orbTexture?: OrbTextureName;
  orbLayoutId?: string;
}

export default function SpeechEngineEnableButton({
  className,
  onPress,
  orbTexture = "agents-1",
  orbLayoutId,
}: SpeechEngineEnableButtonProps) {
  const t = useTranslations("ClientKeys.speech_engine_demo");

  return (
    <Button
      type='button'
      onPress={onPress}
      aria-label={t("enable_button")}
      className={cn(
        "tw-relative tw-flex tw-h-16 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-outline-none",
        // Below md: stretch to fill the parent (parent provides 20px-from-edges padding on mobile).
        "tw-w-full tw-px-6",
        // sm and up: extra horizontal padding once the orb is visible.
        "sm:tw-px-10",
        // md and up: cap the button width to 420px so it doesn't span huge desktop containers.
        "md:tw-max-w-[420px]",
        "tw-text-black tw-type-base tw-whitespace-nowrap",
        "tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_2px_4px_0px_rgba(0,0,0,0.04),0px_6px_16px_0px_rgba(0,0,0,0.04)]",
        "tw-transition-[background-color,transform] tw-duration-300 tw-ease-out enabled:hover:tw-bg-gray-50 enabled:active:tw-scale-[0.98] disabled:tw-cursor-not-allowed disabled:tw-opacity-40 focus-visible:tw-outline-focus",
        className,
      )}
    >
      <m.span
        layoutId={orbLayoutId}
        className='tw-pointer-events-none tw-absolute tw-left-[10px] tw-inset-y-0 tw-my-auto tw-size-11 tw-overflow-hidden tw-rounded-full tw-ring-0.5 tw-ring-inset tw-ring-black/7.5 max-sm:tw-hidden'
        aria-hidden
      >
        <Orb texture={orbTexture} fadeInDuration={0.35} width={44} height={44} exposure={0.09} />
      </m.span>
      {t("enable_button")}
    </Button>
  );
}
